import React, { useState, useRef, useMemo } from 'react';
import { toast } from 'sonner';
import { snapshotService, SnapshotValidationResult } from '../utils/snapshotService';
import { useAppContext } from '../utils/AppContext';
import './SnapshotManager.css';

// Parse seguro: devuelve null en vez de lanzar si el JSON está corrupto.
const safeParse = <T,>(raw: string): T | null => {
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
};

interface SnapshotManagerProps {
  onSnapshotImported?: () => void;
}

const SnapshotManager: React.FC<SnapshotManagerProps> = ({ onSnapshotImported }) => {
  const { selectedTemplate, refreshConfig } = useAppContext();
  const [isExporting, setIsExporting] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [exportStatus, setExportStatus] = useState<string>('');
  const [importStatus, setImportStatus] = useState<string>('');
  const [importError, setImportError] = useState<string>('');
  const [validationResult, setValidationResult] = useState<SnapshotValidationResult | null>(null);
  const [showBackups, setShowBackups] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Función para exportar snapshot
  const handleExport = async (description?: string) => {
    setIsExporting(true);
    setExportStatus('📸 Capturando estado de todos los componentes...');
    
    try {
      const snapshotData = await snapshotService.exportSnapshot(description);

      const snapshot = safeParse<any>(snapshotData);
      const configSections = snapshot?.config ? Object.keys(snapshot.config).length : 0;
      const stateSections = snapshot?.state ? Object.keys(snapshot.state).length : 0;
      const localStorageKeys = snapshot?.localStorage ? Object.keys(snapshot.localStorage).length : 0;
      
      // Generar nombre de archivo con timestamp
      const now = new Date();
      const timestamp = now.toISOString().slice(0, 19).replace(/:/g, '-');
      const filename = `neurolimpiadas_snapshot_${timestamp}.json`;
      
      // Descargar archivo
      snapshotService.downloadSnapshot(snapshotData, filename);
      
      setExportStatus(
        `✅ Snapshot exportado exitosamente: ${filename}\n` +
        `📊 Incluye ${configSections} configuraciones, ${stateSections} estados, ${localStorageKeys} datos locales`
      );
      
      setTimeout(() => {
        setExportStatus('');
      }, 5000);
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Error desconocido';
      setExportStatus(`❌ Error al exportar: ${errorMessage}`);
      toast.error(`Error al exportar: ${errorMessage}`);
      setTimeout(() => setExportStatus(''), 7000);
    } finally {
      setIsExporting(false);
    }
  };

  // Función para manejar selección de archivo
  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setImportError('');
    setValidationResult(null);
    setImportStatus('Validando archivo...');

    try {
      // Verificar que sea un archivo JSON
      if (!file.name.endsWith('.json')) {
        throw new Error('Por favor selecciona un archivo .json');
      }

      // Leer contenido del archivo
      const content = await snapshotService.readSnapshotFile(file);
      
      // Validar el snapshot
      const validation = snapshotService.validateSnapshot(content);
      setValidationResult(validation);
      
      if (!validation.isValid) {
        setImportError(`Archivo inválido: ${validation.errors.join(', ')}`);
        setImportStatus('');
        return;
      }

      if (validation.warnings.length > 0) {
        setImportStatus(`⚠️ Archivo válido con ${validation.warnings.length} advertencias`);
      } else {
        const snapshot = safeParse<any>(content) ?? {};
        const configSections = Object.keys(snapshot.config || {}).length;
        const stateSections = Object.keys(snapshot.state || {}).length;
        const hasAdditionalData = !!snapshot.additionalData;
        
        setImportStatus(
          `✅ Archivo válido y listo para importar\n` +
          `📊 Contiene ${configSections} configuraciones, ${stateSections} estados` +
          (hasAdditionalData ? ', y datos adicionales' : '')
        );
      }

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Error al leer archivo';
      setImportError(errorMessage);
      setImportStatus('');
    }
  };

  // Función para importar snapshot
  const handleImport = async () => {
    const file = fileInputRef.current?.files?.[0];
    if (!file || !validationResult?.isValid) return;

    setIsImporting(true);
    setImportStatus('Importando snapshot...');

    try {
      const content = await snapshotService.readSnapshotFile(file);
      await snapshotService.importSnapshot(content);

      const snapshot = safeParse<any>(content) ?? {};
      const configSections = Object.keys(snapshot.config || {}).length;
      const stateSections = Object.keys(snapshot.state || {}).length;
      
      setImportStatus(
        `✅ Snapshot importado exitosamente\n` +
        `📊 Restauradas ${configSections} configuraciones y ${stateSections} estados\n` +
        `🔄 La página se recargará automáticamente...`
      );
      
      // Refrescar configuración en el contexto
      refreshConfig();
      
      // Notificar a componente padre
      if (onSnapshotImported) {
        onSnapshotImported();
      }

      // Recargar la página después de un breve delay para aplicar todos los cambios
      setTimeout(() => {
        window.location.reload();
      }, 2000);

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Error desconocido';
      setImportError(`Error al importar: ${errorMessage}`);
      setImportStatus('');
      toast.error(`Error al importar: ${errorMessage}`);
    } finally {
      setIsImporting(false);
    }
  };

  // Función para mostrar/ocultar backups
  const toggleBackups = () => {
    setShowBackups(!showBackups);
  };

  // Función para restaurar desde backup
  const handleRestoreBackup = async (backupKey: string) => {
    if (!window.confirm('¿Estás seguro de que quieres restaurar este backup? Los datos actuales se perderán.')) {
      return;
    }

    setIsImporting(true);
    setImportStatus('Restaurando backup...');

    try {
      await snapshotService.restoreFromBackup(backupKey);
      setImportStatus('✅ Backup restaurado exitosamente');
      
      // Refrescar configuración en el contexto
      refreshConfig();
      
      setTimeout(() => {
        window.location.reload();
      }, 1500);

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Error desconocido';
      setImportError(`Error al restaurar backup: ${errorMessage}`);
      setImportStatus('');
      toast.error(`Error al restaurar backup: ${errorMessage}`);
    } finally {
      setIsImporting(false);
    }
  };

  // Memoizamos para evitar recorrer localStorage en cada render; sólo cambia
  // cuando el usuario hace una acción que afecta a los backups.
  const [backupsRefreshKey] = useState(0);
  const backups = useMemo(
    () => snapshotService.getAvailableBackups(),
    // backupsRefreshKey es una señal para invalidar: incrementarlo fuerza
    // recomputación tras import/restore. No es una dependencia «no usada».
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [backupsRefreshKey]
  );

  return (
    <div className="snapshot-manager">
      <div className="snapshot-section">
        <h3>📸 Exportar Snapshot</h3>
        <p>Exporta el estado completo de la aplicación (configuraciones, clasificaciones, brackets, etc.)</p>
        
        <div className="export-controls">
          <button
            onClick={() => handleExport()}
            disabled={isExporting}
            className="export-btn"
          >
            {isExporting ? '⏳ Exportando...' : '📤 Exportar Sistema Completo'}
          </button>
        </div>

        {exportStatus && (
          <div className={`status-message ${exportStatus.includes('❌') ? 'error' : 'success'}`}>
            {exportStatus}
          </div>
        )}
      </div>

      <div className="snapshot-section">
        <h3>📥 Importar Snapshot</h3>
        <p>Importa un snapshot para restaurar el estado completo de la aplicación</p>
        
        <div className="import-controls">
          <input
            ref={fileInputRef}
            type="file"
            accept=".json"
            onChange={handleFileSelect}
            className="file-input"
          />
          
          {validationResult?.isValid && (
            <button
              onClick={handleImport}
              disabled={isImporting}
              className="import-btn"
            >
              {isImporting ? '⏳ Importando...' : '📥 Importar Snapshot'}
            </button>
          )}
        </div>

        {importStatus && (
          <div className={`status-message ${importStatus.includes('❌') ? 'error' : 'success'}`}>
            {importStatus}
          </div>
        )}

        {importError && (
          <div className="status-message error">
            ❌ {importError}
          </div>
        )}

        {validationResult && (
          <div className="validation-result">
            <h4>Resultado de Validación:</h4>
            <div className={`validation-status ${validationResult.isValid ? 'valid' : 'invalid'}`}>
              {validationResult.isValid ? '✅ Archivo válido' : '❌ Archivo inválido'}
            </div>
            
            {validationResult.warnings.length > 0 && (
              <div className="warnings">
                <strong>⚠️ Advertencias:</strong>
                <ul>
                  {validationResult.warnings.map((warning, index) => (
                    <li key={index}>{warning}</li>
                  ))}
                </ul>
              </div>
            )}
            
            {validationResult.errors.length > 0 && (
              <div className="errors">
                <strong>❌ Errores:</strong>
                <ul>
                  {validationResult.errors.map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>

      {backups.length > 0 && (
        <div className="snapshot-section">
          <h3>
            🗂️ Backups Automáticos
            <button
              onClick={toggleBackups}
              className="toggle-backups-btn"
            >
              {showBackups ? '🔼' : '🔽'}
            </button>
          </h3>
          
          {showBackups && (
            <div className="backups-list">
              <p>Se crean automáticamente antes de cada importación:</p>
              {backups.map((backup) => (
                <div key={backup.key} className="backup-item">
                  <span className="backup-date">{backup.date}</span>
                  <button
                    onClick={() => handleRestoreBackup(backup.key)}
                    className="restore-backup-btn"
                    disabled={isImporting}
                  >
                    🔄 Restaurar
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="snapshot-info">
        <h4>ℹ️ Información del Sistema Actual</h4>
        <div className="system-info">
          <div><strong>Plantilla:</strong> {selectedTemplate}</div>
          <div><strong>Fecha:</strong> {new Date().toLocaleString('es-ES')}</div>
          <div><strong>Configuraciones incluidas:</strong></div>
          <ul>
            <li>⏱️ Timer: Tiempos, títulos, subtítulos, categorías</li>
            <li>🏆 Bracket: Equipos, fases, campos, configuraciones de torneo</li>
            <li>📊 Clasificación: Teams, puntuaciones por fases, nombres de fases, títulos</li>
            <li>📝 Pizarra: Configuraciones de equipos, colores, números, puntuaciones, títulos</li>
            <li>🏆 Podium: Ganadores, imágenes, títulos, subtítulos, configuraciones de slides</li>
            <li>🎬 Carrusel: Media files, configuraciones de reproducción, transiciones</li>
            <li>🎯 Plantilla de equipos: Selección actual y configuraciones regionales</li>
            <li>📋 Estados de formularios: Todos los campos de texto, selectores, checkboxes</li>
            <li>💾 LocalStorage: Todos los datos persistentes de la aplicación</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default SnapshotManager;